import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import OfferCard from "./OfferCard";
import EventCard from "./EventCard";
import { supabase } from "../utils/supabase";
import WelcomeHeader from "./WelcomeHeader";

const Dashboard = ({ userProfile }) => {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [events, setEvents] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchOffers(), fetchEvents(), fetchBenefits()]);
      setLoading(false);
    };

    loadData();
  }, []);

  const fetchOffers = async () => {
    try {
      const { data, error } = await supabase
        .from("offers")
        .select("*")
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching offers:", error);
      } else {
        const transformedOffers = (data || []).map((offer) => ({
          id: offer.id,
          title: offer.title,
          description: offer.description,
          image:
            offer.image_url ||
            "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&h=600&fit=crop",
          type: "descuento",
          category: "Ofertas",
          price: offer.price ? parseFloat(offer.price) : undefined,
          originalPrice: offer.original_price
            ? parseFloat(offer.original_price)
            : undefined,
          discount: offer.discount,
          start_date: offer.start_date,
          end_date: offer.end_date,
        }));
        setOffers(transformedOffers);
      }
    } catch (error) {
      console.error("Error fetching offers:", error);
    }
  };

  const fetchEvents = async () => {
    try {
      const { data, error } = await supabase
        .from("events")
        .select("*")
        .order("event_date", { ascending: true });

      if (error) {
        console.error("Error fetching events:", error);
        setEvents(getExampleEvents());
      } else {
        const transformedEvents = (data || []).map((event) => ({
          id: event.id,
          title: event.title,
          description: event.description,
          image_url:
            event.image_url ||
            "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=800&h=600&fit=crop",
          type: "evento",
          category: "Eventos",
          event_date: event.event_date,
          location: event.location,
          capacity: event.capacity,
          price: event.price,
          is_free: event.is_free || false,
          registration_required: event.registration_required || false,
        }));

        if (transformedEvents.length === 0) {
          setEvents(getExampleEvents());
        } else {
          setEvents(transformedEvents);
        }
      }
    } catch (error) {
      console.error("Error fetching events:", error);
      setEvents(getExampleEvents());
    }
  };

  const fetchBenefits = async () => {
    console.log("🔍 Iniciando fetchBenefits...");
    try {
      console.log("📡 Haciendo consulta a Supabase benefits...");
      const { data, error } = await supabase
        .from("benefits")
        .select("*")
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("❌ Error fetching benefits:", error);
        console.log("🔄 Usando benefits de ejemplo...");
        setBenefits(getExampleBenefits());
      } else {
        console.log("✅ Benefits obtenidos exitosamente:", data);
        const transformedBenefits = (data || []).map((benefit) => ({
          id: benefit.id,
          title: benefit.title,
          description: benefit.description,
          image_url:
            benefit.image_url ||
            "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&h=600&fit=crop",
          is_active: benefit.is_active,
        }));

        console.log({ transformedBenefits });

        console.log("🔄 Benefits transformados:", transformedBenefits);

        if (transformedBenefits.length === 0) {
          console.log("⚠️ No hay benefits en BD, usando ejemplos...");
          setBenefits(getExampleBenefits());
        } else {
          console.log("💾 Guardando benefits en estado...");
          setBenefits(transformedBenefits);
        }
      }
    } catch (error) {
      console.error("💥 Error inesperado fetching benefits:", error);
      console.log("🔄 Usando benefits de ejemplo por error...");
      setBenefits(getExampleBenefits());
    }
    console.log("🏁 fetchBenefits completado");
  };

  const getExampleBenefits = () => {
    return [
      {
        id: "example-1",
        title: "Descuentos VIP",
        description:
          "Acceso a ofertas y promociones únicas solo para miembros del club.",
        image_url:
          "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=400&fit=crop",
        is_active: true,
      },
      {
        id: "example-2",
        title: "Eventos Exclusivos",
        description:
          "Invitaciones a degustaciones, talleres y lanzamientos exclusivos.",
        image_url:
          "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=600&h=400&fit=crop",
        is_active: true,
      },
      {
        id: "example-3",
        title: "Productos Premium",
        description:
          "Prioridad en la compra de productos de temporada y orgánicos.",
        image_url:
          "https://images.unsplash.com/photo-1542838132-92c53300491e?w=600&h=400&fit=crop",
        is_active: true,
      },
      {
        id: "example-4",
        title: "Atención Personalizada",
        description:
          "Soporte y asesoramiento dedicado para todas tus necesidades.",
        image_url:
          "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&h=400&fit=crop",
        is_active: true,
      },
    ];
  };

  const getExampleEvents = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);
    const nextMonth = new Date(today);
    nextMonth.setMonth(nextMonth.getMonth() + 1);

    return [
      {
        id: "example-1",
        title: "Degustación de Vinos Premium",
        description:
          "Únete a nuestra exclusiva degustación de vinos premium seleccionados especialmente para los miembros de Club Mia.",
        image_url:
          "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=800&h=600&fit=crop",
        type: "evento",
        category: "Eventos",
        event_date: tomorrow.toISOString(),
        location: "Supermercados Mia - Sede Principal",
        capacity: 25,
        price: 45,
        is_free: false,
        registration_required: true,
      },
      {
        id: "example-2",
        title: "Clase de Cocina: Platos Gourmet",
        description:
          "Aprende a preparar deliciosos platos gourmet con nuestro chef especializado.",
        image_url:
          "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&h=600&fit=crop",
        type: "evento",
        category: "Eventos",
        event_date: nextWeek.toISOString(),
        location: "Centro Culinario Mia",
        capacity: 15,
        price: null,
        is_free: true,
        registration_required: true,
      },
      {
        id: "example-3",
        title: "Festival de Productos Orgánicos",
        description:
          "Celebra con nosotros el Festival de Productos Orgánicos. Conoce a productores locales.",
        image_url:
          "https://images.unsplash.com/photo-1542838132-92c53300491e?w=800&h=600&fit=crop",
        type: "evento",
        category: "Eventos",
        event_date: nextMonth.toISOString(),
        location: "Plaza Central Mia",
        capacity: 100,
        price: null,
        is_free: true,
        registration_required: false,
      },
    ];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 pt-16 sm:pt-24 px-3 sm:px-4 pb-6 sm:pb-8">
      <div className="max-w-7xl mx-auto space-y-6 sm:space-y-8 mt-8 sm:mt-16">
        <WelcomeHeader userProfile={userProfile} />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto"
        >
          <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl p-4 sm:p-6 text-center">
            <div className="text-2xl sm:text-3xl font-bold text-orange-600 mb-2">
              {offers.length}
            </div>
            <div className="text-gray-600 text-sm sm:text-base">
              Ofertas Activas
            </div>
          </div>
          <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl p-4 sm:p-6 text-center">
            <div className="text-2xl sm:text-3xl font-bold text-blue-600 mb-2">
              {events.length}
            </div>
            <div className="text-gray-600 text-sm sm:text-base">
              Eventos Próximos
            </div>
          </div>
          {/* <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl p-6 text-center">
            <div className="text-3xl font-bold text-green-600 mb-2">
              {events.filter(e => e.is_free).length}
            </div>
            <div className="text-gray-600">Eventos Gratuitos</div>
          </div> */}
        </motion.div>

        {/* Ofertas destacadas */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="space-y-6"
        >
          <div className="flex items-center justify-between px-2">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
              Ofertas Destacadas
            </h2>
            <motion.button
              onClick={() => navigate("/ofertas")}
              className="text-orange-600 hover:text-orange-700 font-medium text-sm sm:text-base"
              whileHover={{ scale: 1.05 }}
            >
              <span className="hidden sm:inline">Ver todas →</span>
              <span className="sm:hidden">Ver →</span>
            </motion.button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 items-stretch">
            {offers.length > 0 ? (
              offers
                .slice(0, 3)
                .map((offer, index) => (
                  <OfferCard key={offer.id} offer={offer} index={index} />
                ))
            ) : (
              <div className="col-span-full text-center py-8">
                <p className="text-gray-500">
                  No hay ofertas disponibles en este momento
                </p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Eventos próximos */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="space-y-6"
        >
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-800">
              Próximos Eventos
            </h2>
            <motion.button
              onClick={() => navigate("/eventos")}
              className="text-blue-600 hover:text-blue-700 font-medium"
              whileHover={{ scale: 1.05 }}
            >
              Ver todos →
            </motion.button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
            {events.length > 0 ? (
              events
                .slice(0, 3)
                .map((event, index) => (
                  <EventCard key={event.id} event={event} index={index} />
                ))
            ) : (
              <div className="col-span-full text-center py-8">
                <p className="text-gray-500">
                  No hay eventos próximos en este momento
                </p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Beneficios del Club */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="space-y-6"
        >
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Beneficios de Ser Miembro
            </h2>
            <p className="text-gray-600">
              Disfruta de todas las ventajas exclusivas de Club Mia
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.length > 0 ? (
              benefits.slice(0, 4).map((benefit, index) => (
                <motion.div
                  key={benefit.id}
                  className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl p-6 text-center shadow-lg"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{
                    y: -5,
                    boxShadow: "0 15px 30px rgba(0,0,0,0.1)",
                  }}
                >
                  {benefit.image_url && (
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full overflow-hidden">
                      <img
                        src={benefit.image_url}
                        alt={benefit.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src =
                            "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&h=400&fit=crop";
                        }}
                      />
                    </div>
                  )}
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-gray-600">{benefit.description}</p>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-8">
                <p className="text-gray-500">
                  No hay beneficios disponibles en este momento
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;
